import React, { useMemo } from "react"
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  CardMedia,
  Button,
  Avatar,
} from "@mui/material"
import { deepmerge } from "@mui/utils"

import { createOpeningHours } from "utils/date"
import { useTranslation } from "providers/i18n"

import { StoreIcon } from "components/icons"
import { ClockIcon, LayerIcon } from "extensions/one-siam/components/icons"
import { getIcon } from "utils/icon"

const LoadingFeature = () => {
  const theme = useTheme()
  const {
    root,
    titleContainer,
    actionContainer,
    headerContainer,
    infoContainer,
    infoList,
    infoListItem,
  } = theme.components[`FeatureSummary`]?.styleOverrides || {}
  return (
    <Box sx={root}>
      <Box sx={headerContainer}>
        <Box sx={titleContainer}>
          <Skeleton variant="rectangular" width={32} height={32} />
          <Skeleton variant="text" width={120} />
        </Box>
        <Box sx={actionContainer}>
          <Skeleton variant="rounded" width={74} height={32} />
        </Box>
      </Box>

      <Box sx={infoContainer}>
        <Box sx={infoList}>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={70} />
          </Box>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={115} />
          </Box>
          <Box sx={infoListItem}>
            <Skeleton variant="text" width={115} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width={60} height={40} />
      </Box>
    </Box>
  )
}

export const FeatureSummary = ({
  zone,
  logo,
  venueLogo,
  category,
  phone,
  level,
  name,
  venueName,
  hours,
  id,
  loading,
  group,
  onNavigate,
  categoryIconProp = {},
  sx = {},
  ...props
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const {
    close = "",
    open = "",
    isValid,
    openingStatus,
    nextOpeningStatus,
    dayToOpen,
    isOpening,
  } = useMemo(() => createOpeningHours(hours), [hours])

  const theme = useTheme()
  const {
    root,
    title,
    titleContainer,
    venueLogoContainer,
    venueLogoImage,
    logoContainer,
    logoImage,
    categoryIcon,
    actionContainer,
    navigationButton,
    headerContainer,
    infoContainer,
    infoList,
    infoListItem,
    infoText,
    iconWrapper,
  } = theme.components[`FeatureSummary`]?.styleOverrides || {}

  const venueNameText = t(venueName)
  const categoryText = t(category)
  const levelText = level ? t("Level Text", { level: t(level) }) : "-"
  const nameText = t(name)
  const openingHoursText = isValid
    ? `${t(openingStatus)} · ${t(nextOpeningStatus)} ${dayToOpen} ${
        isOpening ? close : open
      }`
    : "-"

  if (loading) return <LoadingFeature />

  return (
    <Box sx={deepmerge(root, sx)} {...props}>
      <Box sx={headerContainer}>
        <Box sx={titleContainer}>
          <Box sx={logoContainer} className="FeatureSummary-logoContainer">
            <Avatar
              sx={logoImage}
              className="FeatureSummary-logoImage"
              src={logo || categoryIconProp?.image}
            >
              {getIcon(categoryIconProp, categoryIcon, {
                imageIconProps: {
                  solidColorFilterId: "placeDetail_avatar_filter",
                },
              }) || <StoreIcon />}
            </Avatar>
          </Box>
          <Typography variant="title" component="h3" sx={title}>
            {nameText}
          </Typography>
        </Box>
      </Box>
      <Box sx={infoContainer}>
        <Box sx={infoList}>
          <Box
            sx={infoListItem}
            className="FeatureSummary-infoListItem--category"
          >
            <Typography
              variant="subtitle2"
              className="FeatureSummary-infoText"
              sx={infoText}
            >
              {categoryText}
            </Typography>
          </Box>
          <Box sx={infoListItem} className="FeatureSummary-infoListItem--venue">
            <Typography
              variant="subtitle2"
              className="FeatureSummary-infoText"
              sx={infoText}
            >
              {venueNameText}
            </Typography>
          </Box>
          <Box sx={infoListItem} className="FeatureSummary-infoListItem--level">
            <Box sx={iconWrapper} className="FeatureSummary-iconWrapper">
              <LayerIcon />
            </Box>
            <Typography
              variant="subtitle2"
              className="FeatureSummary-infoText FeatureSummary-levelText"
              sx={infoText}
            >
              {levelText}
            </Typography>
          </Box>
          <Box
            sx={infoListItem}
            className="FeatureSummary-infoListItem--opening-hours"
          >
            <Box sx={iconWrapper} className="FeatureSummary-iconWrapper">
              <ClockIcon />
            </Box>
            <Typography
              variant="subtitle2"
              className="FeatureSummary-infoText"
              sx={infoText}
            >
              {openingHoursText}
            </Typography>
          </Box>
        </Box>
        {venueLogo && (
          <Box sx={venueLogoContainer}>
            <CardMedia component="img" image={venueLogo} sx={venueLogoImage} />
          </Box>
        )}
      </Box>
      <Box sx={actionContainer}>
        <Button
          variant="contained"
          className="FeatureSummary-navigationButton"
          onClick={onNavigate}
          sx={navigationButton}
        >
          {t("Get direction")}
        </Button>
      </Box>
    </Box>
  )
}
